import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { MaskLine } from "../lib/motion";
import { PORTRAIT } from "../data";

export const Hero = ({ onBook }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-screen pt-32 pb-16 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        {/* Text */}
        <div className="lg:col-span-7 relative z-10">
          <h1 className="font-serif font-medium text-[var(--ink)] leading-[0.98] tracking-tight text-5xl sm:text-6xl xl:text-7xl">
            <MaskLine delay={0.45}>Costruisco chiarezza</MaskLine>
            <MaskLine delay={0.6} className="italic text-[var(--petrol)]">dove la complessità</MaskLine>
            <MaskLine delay={0.75}>rallenta le decisioni.</MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.9 }}
            className="mt-8 max-w-xl text-lg text-[var(--ink-soft)] leading-relaxed"
          >
            Board Advisor ed Executive Advisor. Affianco CEO e Consigli di Amministrazione nel governo
            della complessità — AI, rischio e trasformazione digitale — trasformandola in decisioni
            più solide e valore duraturo.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.15, duration: 0.9 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <button data-testid="hero-book-btn" onClick={onBook}
              className="btn-fill text-xs font-semibold uppercase tracking-[0.15em] bg-[var(--petrol)] text-white px-8 py-4">
              Prenota una Executive Call
            </button>
            <a href="#academy" data-testid="hero-academy-btn"
              className="btn-fill text-xs font-semibold uppercase tracking-[0.15em] border border-[var(--anthracite)] text-[var(--anthracite)] hover:text-white px-8 py-4 inline-flex items-center">
              Scopri l'Academy
            </a>
          </motion.div>
        </div>

        {/* Portrait */}
        <div className="lg:col-span-5 relative">
          <motion.div style={{ y }} className="relative">
            <motion.div
              initial={{ clipPath: "inset(100% 0 0 0)" }}
              animate={{ clipPath: "inset(0% 0 0 0)" }}
              transition={{ duration: 1.3, ease: [0.22, 1, 0.36, 1], delay: 0.5 }}
              className="relative aspect-[3/4] overflow-hidden bg-[var(--paper-2)]"
            >
              <motion.img style={{ scale }} src={PORTRAIT} alt="Maria Gabriella Ritondo"
                className="w-full h-full object-cover object-top" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2, duration: 0.9 }}
              className="absolute -bottom-5 -left-4 sm:-left-8 max-w-[80%] border border-[var(--line)] bg-white px-6 py-4 shadow-[0_18px_40px_-24px_rgba(30,26,20,0.5)]">
              <div className="overline">Maria Gabriella Ritondo</div>
              <div className="mt-2 text-[0.65rem] uppercase tracking-[0.2em] text-[var(--ink-soft)]">Board Advisor · AI Governance · Digital Transformation</div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 }}
        className="max-w-7xl mx-auto px-6 sm:px-12 mt-16 flex items-center gap-3 text-[var(--ink-soft)]"
      >
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2 }}>
          <ArrowDown size={16} strokeWidth={1.5} />
        </motion.span>
        <span className="text-xs uppercase tracking-[0.2em]">Scorri per esplorare</span>
      </motion.div>
    </section>
  );
};
